import { ComponentProps } from 'preact';
import { useLayoutEffect, Ref } from 'preact/hooks';
import { calculateLineHeight, getStylingHeight } from './ScrollHeightUtils';
import { TextAreaAutosize } from './TextAreaAutosize';

type PickedPropsFromTextAreaAutosize = Pick<
  ComponentProps<typeof TextAreaAutosize>,
  'minRows' | 'maxRows' | 'value'
>;
type Props = PickedPropsFromTextAreaAutosize & {
  isReadonly?: boolean;
  enabledElementRef: Ref<HTMLTextAreaElement>;
  readonlyElementRef: Ref<HTMLTextAreaElement | HTMLDivElement>;
};

// TODO: Make this fire when the container resizes - JET-50840
export const useTextAreaAutosizing = ({
  isReadonly,
  enabledElementRef,
  readonlyElementRef,
  minRows = 2,
  maxRows,
  value
}: Props) => {
  useLayoutEffect(() => {
    const textareaRef = isReadonly ? readonlyElementRef : enabledElementRef;
    if (textareaRef.current === null) return;
    if (textareaRef.current instanceof HTMLDivElement) return; // We don't need to do anything if the readonlyElement is a DIV.
    const textarea = textareaRef.current;
    const lineHeight = calculateLineHeight(textarea);
    textarea.style.height = '0';
    const { paddingHeight, borderHeight } = getStylingHeight(textarea);
    const heightForMinRows = lineHeight * minRows + paddingHeight + borderHeight;
    const scrollHeight = textarea.scrollHeight + borderHeight;
    let resizedHeight = 0;
    // if maxRows is -1 the textarea will grow or shrink to fit all the content.
    // it won't shrink any less than rows.
    if (maxRows === undefined) {
      // we want to fit the entire scrollHeight, but we don't want
      // to shrink smaller than the height for rows.
      if (scrollHeight < heightForMinRows) {
        resizedHeight = heightForMinRows;
      } else {
        resizedHeight = scrollHeight;
      }
    } else if (maxRows > minRows) {
      // if maxRows is positive and greater than rows, the textarea will grow to fit the content
      // up to maxrows, or shrink to fit the content and down to rows.
      const heightForMaxRows = lineHeight * maxRows + paddingHeight + borderHeight;
      if (scrollHeight > heightForMaxRows) {
        resizedHeight = heightForMaxRows;
      } else if (scrollHeight < heightForMinRows) {
        resizedHeight = heightForMinRows;
      } else {
        resizedHeight = scrollHeight;
      }
    } else {
      resizedHeight = heightForMinRows;
    }
    // The 0.5 gaurantees that the clientHeight will be bigger than the scrollHeight, so no scrollbar appears.
    textarea.style.height = resizedHeight + 0.5 + 'px';
  }, [value, minRows, maxRows, isReadonly, enabledElementRef, readonlyElementRef]);
};
